import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import type { SpendingKeys, StoredJoinSplitNote } from "../joinsplit/types";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import { getInboxSecretKeyCandidates } from "../utils/inbox-crypto";
import {
  decryptOnchainNoteRecovery,
  type OnchainNoteRecoveryPayload,
} from "./onchainRecoveryCodec";
import {
  readEvmRecoveryPage,
  type EvmRecoveryRegistryConfig,
  type EvmRecoveryRecord,
} from "./onchainRecoveryEvm";
import { createFlatJoinSplitV2Note } from "./notes";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2TreeScanResult,
} from "./merkleIndex";

const POOL = new ethers.Interface([
  "function currentTreeIndex() view returns(uint256)",
  "function merkleRoot() view returns(bytes32)",
  "function nextLeafIndex() view returns(uint256)",
  "function knownRootTreePlusOne(bytes32) view returns(uint256)",
  "function computeTokenHashForTree(uint256,address,uint8,uint256) view returns(bytes32)",
  "function nullifierUsed(bytes32) view returns(bool)",
  "event PrivateSwap(bytes32[] nullifiers,address inputToken,address outputToken,uint256 inputAmount,uint256 outputAmount,bytes32 outputCommitment)",
  "event PrivateSwapOutput(bytes32 indexed routeHash,address indexed token,bytes32 indexed npk,uint256 amount,bytes32 tokenHash,bytes32 commitment,uint256 treeIndex)",
]);

export interface EvmRecoveryPool {
  address: string;
  deploymentBlock: number;
}
export interface ScanEvmRecoveryParams {
  provider: ethers.Provider;
  registry: EvmRecoveryRegistryConfig;
  spendingKeys: SpendingKeys;
  pools: readonly EvmRecoveryPool[];
  signal?: AbortSignal;
  isCurrent?: () => boolean;
  chunkSize?: number;
  onProgress?: (scannedRecords: number) => void;
  finalityBlocks?: number;
  requireFinalized?: boolean;
}

/** Public registry state + public pool logs + local phrase-derived keys only.
 * A failed/truncated RPC scan throws; it never becomes a zero balance. */
export async function scanEvmOnchainRecovery(params: ScanEvmRecoveryParams) {
  const { provider, registry, spendingKeys } = params;
  const check = () => {
    if (params.signal?.aborted || params.isCurrent?.() === false)
      throw Error("Recovery scan cancelled.");
  };
  check();
  if (!globalThis.crypto?.subtle)
    throw Error("This browser cannot decrypt recovery records.");
  const chunkSize = params.chunkSize ?? 2_000;
  if (!Number.isSafeInteger(chunkSize) || chunkSize < 1 || chunkSize > 10_000)
    throw Error("Invalid recovery scan limits.");
  const network = await provider.getNetwork();
  if (network.chainId !== BigInt(registry.chainId))
    throw Error("Recovery RPC is on another network.");
  const snapshot = await provider.getBlock("latest");
  const depth =
    params.finalityBlocks ?? EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS;
  if (!Number.isSafeInteger(depth) || depth < 0)
    throw Error("Invalid recovery confirmation policy.");
  const finalized =
    snapshot &&
    (await provider.getBlock(
      params.requireFinalized
        ? "finalized"
        : Math.max(0, snapshot.number - depth),
    ));
  if (!snapshot?.hash || !finalized?.hash)
    throw Error("Recovery chain finality is unavailable.");
  const safeBlock = finalized.number,
    safeHash = finalized.hash;
  const pools = new Map(
    params.pools.map((pool) => {
      if (
        !ethers.isAddress(pool.address) ||
        !Number.isSafeInteger(pool.deploymentBlock) ||
        pool.deploymentBlock < 0
      )
        throw Error("Invalid recovery pool configuration.");
      return [ethers.getAddress(pool.address).toLowerCase(), pool] as const;
    }),
  );
  if (pools.size !== params.pools.length)
    throw Error("Duplicate recovery pool configuration.");
  const inboxKeys = getInboxSecretKeyCandidates(spendingKeys.spendingKey);
  const notes = new Map<string, StoredJoinSplitNote>();
  const pending = new Set<string>(),
    covered = new Set<string>();
  const at = async (
    pool: string,
    block: number,
    name: string,
    args: readonly unknown[] = [],
  ) =>
    POOL.decodeFunctionResult(
      name,
      await provider.call({
        to: pool,
        data: POOL.encodeFunctionData(name, args),
        blockTag: block,
      }),
    )[0];
  type Swap = {
    commitment: string;
    token: string;
    amount: bigint;
    npk?: bigint;
  };
  type History = {
    trees: FlatJoinSplitV2TreeScanResult[];
    tokenHashes: Map<string, string>;
    swaps?: Swap[];
    block: number;
  };
  const histories = new Map<string, History>();
  async function history(
    pool: EvmRecoveryPool,
    block: number,
    blockHash: string,
  ): Promise<History> {
    const key = `${pool.address.toLowerCase()}:${block}`;
    const cached = histories.get(key);
    if (cached) return cached;
    const result: History = { trees: [], tokenHashes: new Map(), block };
    if (block < pool.deploymentBlock) {
      histories.set(key, result);
      return result;
    }
    check();
    const [currentRaw, root, countRaw] = await Promise.all([
      at(pool.address, block, "currentTreeIndex"),
      at(pool.address, block, "merkleRoot"),
      at(pool.address, block, "nextLeafIndex"),
    ]);
    const current = Number(currentRaw),
      count = Number(countRaw);
    if (
      !Number.isSafeInteger(current) ||
      current < 0 ||
      !Number.isSafeInteger(count) ||
      count < 0
    )
      throw Error(
        "Recovery pool history exceeds supported scan limits; recovery is incomplete.",
      );
    for (let treeIndex = 0; treeIndex <= current; treeIndex++) {
      check();
      const tree = await scanFlatJoinSplitV2Tree({
        provider: provider as any,
        pool: pool.address,
        treeIndex,
        fromBlock: pool.deploymentBlock,
        toBlock: block,
        chunkSize,
        expectedToBlockHash: blockHash,
        expectedRoot: treeIndex === current ? String(root) : undefined,
        expectedNextLeafIndex: treeIndex === current ? count : undefined,
      });
      if (
        BigInt(
          await at(pool.address, block, "knownRootTreePlusOne", [tree.root]),
        ) !== BigInt(treeIndex + 1)
      )
        throw Error(
          "Recovery history does not match the pool's canonical tree.",
        );
      result.trees.push(tree);
    }
    histories.set(key, result);
    return result;
  }
  async function swaps(pool: EvmRecoveryPool, state: History): Promise<Swap[]> {
    if (!state.swaps) {
      state.swaps = [];
      const topics = [
        POOL.getEvent("PrivateSwap")!.topicHash,
        POOL.getEvent("PrivateSwapOutput")!.topicHash,
      ];
      for (
        let from = pool.deploymentBlock;
        from <= state.block;
        from += chunkSize
      ) {
        check();
        const to = Math.min(state.block, from + chunkSize - 1);
        const logs = await provider.getLogs({
          address: pool.address,
          topics: [topics],
          fromBlock: from,
          toBlock: to,
        });
        for (const log of logs) {
          if (
            log.removed ||
            log.address.toLowerCase() !== pool.address.toLowerCase() ||
            log.blockNumber < from ||
            log.blockNumber > to
          )
            throw Error("Recovery swap history is inconsistent.");
          const event = POOL.parseLog(log)!;
          state.swaps.push(
            event.name === "PrivateSwap"
              ? {
                  commitment: String(event.args.outputCommitment).toLowerCase(),
                  token: ethers.getAddress(event.args.outputToken),
                  amount: BigInt(event.args.outputAmount),
                }
              : {
                  commitment: String(event.args.commitment).toLowerCase(),
                  token: ethers.getAddress(event.args.token),
                  amount: BigInt(event.args.amount),
                  npk: BigInt(event.args.npk),
                },
          );
        }
      }
    }
    return state.swaps;
  }
  async function recoverRecord(
    record: EvmRecoveryRecord,
    payload: OnchainNoteRecoveryPayload,
    pool: EvmRecoveryPool,
  ) {
    const locator = record.locator.toLowerCase(),
      token = ethers.getAddress(ethers.dataSlice(payload.tokenAddress, 12));
    const npk = await computeNPK(spendingKeys.mpk, payload.random);
    if (payload.kind === 2 && ethers.toBeHex(npk, 32).toLowerCase() !== locator)
      return;
    // First use the confirmation snapshot. Only canonical, newer insertions can
    // become pending; public prepublications and unused refund seeds cannot.
    for (const [block, blockHash] of [
      [safeBlock, safeHash],
      [snapshot!.number, snapshot!.hash!],
    ] as const) {
      const state = await history(pool, block, blockHash);
      const possibilities =
        payload.kind === 1
          ? [{ commitment: locator, token, amount: payload.value }]
          : (await swaps(pool, state)).filter(
              (swap) =>
                swap.token === token &&
                (swap.npk === undefined || swap.npk === npk),
            );
      let found = false;
      for (const possibility of possibilities) {
        check();
        const tree = state.trees.find((tree) =>
          tree.tree.locate(possibility.commitment),
        );
        if (!tree) continue;
        const location = tree.tree.locate(possibility.commitment)!;
        const tokenKey = `${tree.treeIndex}:${token}`;
        if (!state.tokenHashes.has(tokenKey))
          state.tokenHashes.set(
            tokenKey,
            String(
              await at(pool.address, block, "computeTokenHashForTree", [
                tree.treeIndex,
                token,
                0,
                0,
              ]),
            ),
          );
        const note = await createFlatJoinSplitV2Note({
          recipientMpk: spendingKeys.mpk,
          random: payload.random,
          chainId: BigInt(registry.chainId),
          poolAddress: pool.address,
          treeIndex: tree.treeIndex,
          tokenAddress: token,
          tokenHash: state.tokenHashes.get(tokenKey)!,
          value: possibility.amount,
        });
        if (note.commitment.toLowerCase() !== possibility.commitment) continue;
        found = true;
        const insertion = tree.insertions.find(
          (insertion) => insertion.commitment === possibility.commitment,
        )!;
        if (block > safeBlock) {
          if (insertion.blockNumber > safeBlock) pending.add(locator);
          continue;
        }
        const nullifier = await computeNullifier(
          spendingKeys.nullifyingKey,
          note.tokenHash,
          location.leafIndex,
        );
        const spent = Boolean(
          await at(pool.address, safeBlock, "nullifierUsed", [nullifier]),
        );
        const id = `${pool.address.toLowerCase()}:${note.commitment.toLowerCase()}`;
        notes.set(id, {
          note: { ...note, leafIndex: location.leafIndex, nullifier },
          spent,
          timestamp: 0,
          txHash: insertion?.transactionHash,
        });
        covered.add(id);
      }
      if (found || block === snapshot!.number) return;
    }
  }
  // Stream fixed-size pages instead of retaining every decryptable public
  // record. Registry growth must never impose a permanent recovery cutoff.
  let cursor = 0n,
    recordCount: bigint | undefined;
  do {
    check();
    const page = await readEvmRecoveryPage(provider, registry, {
      cursor,
      limit: 32,
      blockTag: snapshot.number,
    });
    if (recordCount !== undefined && recordCount !== page.total)
      throw Error("Recovery registry snapshot changed while scanning.");
    recordCount = page.total;
    for (const record of page.records) {
      check();
      const pool = pools.get(record.pool.toLowerCase());
      if (record.kind === 3 || !pool) continue;
      let payload: OnchainNoteRecoveryPayload;
      try {
        payload = await decryptOnchainNoteRecovery(
          ethers.getBytes(record.envelope),
          inboxKeys,
          {
            chain: "evm",
            chainDomain: ethers.toBeHex(registry.chainId, 32),
            pool: record.pool,
            kind: record.kind,
            locator: record.locator,
          },
        );
      } catch {
        continue; /* Other recipients and hostile ciphertext are expected. */
      }
      await recoverRecord(record, payload, pool);
    }
    cursor = page.nextCursor;
    params.onProgress?.(Number(cursor));
    // Let a UI cancellation/account change interrupt a large public scan.
    if (cursor < recordCount)
      await new Promise((resolve) => setTimeout(resolve, 0));
  } while (cursor < recordCount!);
  check();
  const [sameSnapshot, sameSafe] = await Promise.all([
    provider.getBlock(snapshot.number),
    provider.getBlock(safeBlock),
  ]);
  if (sameSnapshot?.hash !== snapshot.hash || sameSafe?.hash !== safeHash)
    throw Error("Recovery chain changed while scanning; retry safely.");
  return {
    notes: [...notes.values()],
    pending: [...pending],
    coveredCommitments: [...covered],
    scannedRecords: Number(cursor),
    safeBlock,
    safeBlockHash: safeHash,
  };
}
